import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Mail, Phone, MapPin, Clock, Sparkles, Heart } from "lucide-react";
import { EASE } from "@/lib/motion";
import { CONTACT, IMAGES } from "@/data/content";
import { Seo } from "@/components/Seo";
import { PayButton } from "@/components/PayButton";
import {
  CATEGORIES,
  getServicesByCategory,
  getService,
  PAYMENT_DEFAULTS,
  NUMEROLOGY_SERVICE_ID,
  PERSONAL_CATEGORY_ID,
} from "@/payments/config";

const blankForm = {
  name: "",
  email: "",
  phone: "",
  categoryId: "",
  serviceId: "",
  message: "",
  dob: "",
  lifePath: "",
  expression: "",
};

const buildPrefill = (bp) => {
  if (!bp) return { ...blankForm };
  const lines = [
    "I would love to unlock my complete personal reading.",
    "",
    bp.firstName ? `First name: ${bp.firstName}` : null,
    bp.dob ? `Date of birth: ${bp.dob}` : null,
    bp.lifePath != null ? `Life Path Number: ${bp.lifePath}` : null,
    bp.expression != null && bp.expression !== "" ? `Expression Number: ${bp.expression}` : null,
  ].filter(Boolean);
  return {
    ...blankForm,
    name: bp.firstName || "",
    categoryId: PERSONAL_CATEGORY_ID,
    serviceId: NUMEROLOGY_SERVICE_ID,
    dob: bp.dob || "",
    lifePath: bp.lifePath != null ? String(bp.lifePath) : "",
    expression: bp.expression != null ? String(bp.expression) : "",
    message: lines.join("\n"),
  };
};

const Contact = () => {
  const location = useLocation();
  const blueprint = location.state?.blueprint || null;

  const [form, setForm] = useState(() => buildPrefill(blueprint));
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleCategory = (categoryId) => {
    // Categories are for browsing — changing one resets the chosen service.
    setForm((f) => ({ ...f, categoryId, serviceId: "" }));
    setErrors((e) => ({ ...e, categoryId: undefined, serviceId: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please share your name";
    if (!form.email.trim()) next.email = "Please share your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email";
    if (!form.categoryId) next.categoryId = "Please choose a category";
    if (!form.serviceId) next.serviceId = "Please select a service";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // ── Future integration point ──────────────────────────────
    // Wire this up to Formspree / EmailJS (or a FastAPI backend) to deliver
    // the enquiry. For now we simply show a calm confirmation state.
    // ──────────────────────────────────────────────────────────
    setSubmitted(true);
  };

  const reset = () => {
    setForm(buildPrefill(blueprint));
    setSubmitted(false);
  };

  const services = form.categoryId ? getServicesByCategory(form.categoryId) : [];
  const selectedService = getService(form.serviceId);
  const isEnquiry = !selectedService || selectedService.paymentType === "enquiry";
  const firstName = form.name.split(" ")[0] || "friend";

  return (
    <div data-testid="contact-page" className="bg-ivory min-h-screen">
      <Seo
        title="Book a Consultation — Svara Chakra"
        description="Reserve your Svara Chakra session — personal wellness consultations in music therapy, numerology, reiki and more, or enquire about corporate wellness, retreats and learning."
        path="/contact"
      />
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-36 md:pt-44 pb-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left column — info + atmosphere */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="kicker text-gold mb-6">
                {blueprint ? "Your Numerology Consultation" : "Book a Consultation"}
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="font-serif font-light text-charcoal text-5xl md:text-6xl leading-[1.02] tracking-tight">
                {blueprint
                  ? "Let us read your complete blueprint."
                  : "Let us design a journey around you."}
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-charcoal/70 leading-relaxed max-w-md md:text-lg">
                {blueprint
                  ? "We have carried your details across — simply add your email and reserve your session below."
                  : "Choose the experience that calls to you. We will respond personally, with warmth and discretion."}
              </p>
            </Reveal>

            <Reveal delay={0.18}>
              {blueprint ? (
                <div
                  data-testid="contact-blueprint-summary"
                  className="mt-12 border border-stone bg-sage/15 p-8"
                >
                  <p className="kicker text-gold mb-6 flex items-center gap-3">
                    <Sparkles size={14} strokeWidth={1.5} /> Your blueprint preview
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="kicker text-charcoal/50 text-[0.58rem] mb-2">Life Path</p>
                      <p className="font-serif font-light text-forest text-5xl leading-none" data-testid="summary-lifepath">
                        {blueprint.lifePath}
                      </p>
                    </div>
                    <div>
                      <p className="kicker text-charcoal/50 text-[0.58rem] mb-2">Expression</p>
                      <p className="font-serif font-light text-forest text-5xl leading-none" data-testid="summary-expression">
                        {blueprint.expression != null && blueprint.expression !== "" ? blueprint.expression : "\u2014"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-stone/70 space-y-2 text-sm text-charcoal/70">
                    {blueprint.firstName && (
                      <p><span className="text-charcoal/45">Name · </span>{blueprint.firstName}</p>
                    )}
                    {blueprint.dob && (
                      <p><span className="text-charcoal/45">Date of birth · </span>{blueprint.dob}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-12 relative overflow-hidden aspect-[16/10]">
                  <img
                    src="/images/contact-lotus.jpg"
                    alt="Golden lotus on tranquil water"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-forest/15" aria-hidden="true" />
                </div>
              )}
            </Reveal>

            <Reveal delay={0.24}>
              <ul className="mt-12 space-y-6">
                <li className="flex items-center gap-4">
                  <Mail size={18} strokeWidth={1.4} className="text-gold shrink-0" />
                  <a href={`mailto:${CONTACT.email}`} className="text-charcoal hover:text-forest transition-colors duration-300">
                    {CONTACT.email}
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <Phone size={18} strokeWidth={1.4} className="text-gold shrink-0" />
                  <span className="text-charcoal">{CONTACT.phone}</span>
                </li>
                <li className="flex items-center gap-4">
                  <MapPin size={18} strokeWidth={1.4} className="text-gold shrink-0" />
                  <span className="text-charcoal">{CONTACT.location}</span>
                </li>
                <li className="flex items-center gap-4">
                  <Clock size={18} strokeWidth={1.4} className="text-gold shrink-0" />
                  <span className="text-charcoal">{CONTACT.hours}</span>
                </li>
              </ul>
            </Reveal>
          </div>

          {/* Right column — form / result */}
          <div className="lg:col-span-6 lg:col-start-7">
            <AnimatePresence mode="wait">
              {submitted && isEnquiry ? (
                /* ── Enquiry confirmation (Corporate / Retreats / Learning) ── */
                <motion.div
                  key="enquiry"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  data-testid="contact-enquiry-message"
                  className="border border-stone bg-sage/20 px-8 md:px-14 py-20 md:py-28 text-center"
                >
                  <div className="mx-auto mb-8 h-16 w-16 rounded-full bg-forest flex items-center justify-center">
                    <Heart size={26} strokeWidth={1.5} className="text-ivory" />
                  </div>
                  <h2 className="font-serif font-light text-charcoal text-3xl md:text-4xl leading-tight">
                    Thank you for your interest.
                  </h2>
                  <p className="mt-6 text-charcoal/70 leading-relaxed max-w-md mx-auto">
                    {PAYMENT_DEFAULTS.enquiryMessage}
                  </p>
                  <button
                    onClick={reset}
                    data-testid="contact-reset"
                    className="mt-10 border border-charcoal/40 text-charcoal px-8 py-3 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-charcoal hover:text-ivory"
                  >
                    Send another note
                  </button>
                </motion.div>
              ) : submitted ? (
                /* ── Booking confirmation (Personal Wellness → reserve) ── */
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  data-testid="contact-success"
                  className="border border-stone bg-sage/20 px-8 md:px-12 py-16 md:py-20 text-center"
                >
                  <div className="mx-auto mb-8 h-16 w-16 rounded-full bg-forest flex items-center justify-center">
                    <Check size={28} strokeWidth={1.5} className="text-ivory" />
                  </div>
                  <h2 className="font-serif font-light text-charcoal text-3xl md:text-4xl leading-tight">
                    A beautiful choice, {firstName}.
                  </h2>
                  <p className="mt-5 text-charcoal/70 leading-relaxed max-w-md mx-auto">
                    Reserve your preferred date and time below. We will be in touch
                    personally to hold the space for you.
                  </p>

                  <div className="mt-10 text-left">
                    <PayButton
                      serviceId={form.serviceId}
                      customer={{ name: form.name, email: form.email, phone: form.phone }}
                    />
                  </div>

                  <button
                    onClick={reset}
                    data-testid="contact-reset"
                    className="mt-8 border border-charcoal/40 text-charcoal px-8 py-3 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-charcoal hover:text-ivory"
                  >
                    Send another note
                  </button>
                </motion.div>
              ) : (
                /* ── Form ── */
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  onSubmit={handleSubmit}
                  noValidate
                  data-testid="contact-form"
                  className="space-y-10"
                >
                  <Field label="Name" error={errors.name}>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your full name"
                      data-testid="contact-name"
                      className="w-full bg-transparent border-b border-stone py-4 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest transition-colors duration-300 rounded-none"
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <Field label="Email" error={errors.email}>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@email.com"
                        data-testid="contact-email"
                        className="w-full bg-transparent border-b border-stone py-4 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest transition-colors duration-300 rounded-none"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="Optional"
                        data-testid="contact-phone"
                        className="w-full bg-transparent border-b border-stone py-4 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest transition-colors duration-300 rounded-none"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                    <Field label="Category" error={errors.categoryId}>
                      <Select value={form.categoryId} onValueChange={handleCategory}>
                        <SelectTrigger
                          data-testid="contact-category"
                          className="w-full bg-transparent border-0 border-b border-stone rounded-none py-4 h-auto px-0 text-charcoal focus:ring-0 focus:border-forest data-[placeholder]:text-charcoal/40 shadow-none"
                        >
                          <SelectValue placeholder="Browse a category" />
                        </SelectTrigger>
                        <SelectContent className="bg-ivory border border-stone rounded-none">
                          {CATEGORIES.map((cat) => (
                            <SelectItem
                              key={cat.id}
                              value={cat.id}
                              data-testid={`category-option-${cat.id}`}
                              className="text-charcoal focus:bg-sage/30 rounded-none cursor-pointer"
                            >
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Service" error={errors.serviceId}>
                      <Select
                        value={form.serviceId}
                        onValueChange={(v) => update("serviceId", v)}
                        disabled={!form.categoryId}
                      >
                        <SelectTrigger
                          data-testid="contact-service"
                          className="w-full bg-transparent border-0 border-b border-stone rounded-none py-4 h-auto px-0 text-charcoal focus:ring-0 focus:border-forest data-[placeholder]:text-charcoal/40 shadow-none disabled:opacity-50"
                        >
                          <SelectValue placeholder={form.categoryId ? "Select a service" : "Choose a category first"} />
                        </SelectTrigger>
                        <SelectContent className="bg-ivory border border-stone rounded-none max-h-72">
                          {services.map((svc) => (
                            <SelectItem
                              key={svc.id}
                              value={svc.id}
                              data-testid={`service-option-${svc.id}`}
                              className="text-charcoal focus:bg-sage/30 rounded-none cursor-pointer"
                            >
                              {svc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Message">
                    <textarea
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      rows={4}
                      placeholder="Share what you are seeking (optional)"
                      data-testid="contact-message"
                      className="w-full bg-transparent border-b border-stone py-4 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest transition-colors duration-300 rounded-none resize-none"
                    />
                  </Field>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-forest text-ivory px-12 py-5 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-charcoal"
                  >
                    Continue
                    <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <label className="kicker text-charcoal/60 text-[0.62rem] block mb-1">{label}</label>
    {children}
    {error && (
      <p className="mt-2 text-xs text-destructive tracking-wide" data-testid="field-error">
        {error}
      </p>
    )}
  </div>
);

export default Contact;
